import { z } from 'zod';

/**
 * Money is transported as `{ amount: string, currency: ISO 4217 }`. The string preserves
 * DECIMAL(19,4) precision through JSON. NEVER convert `amount` to Number for math.
 */
export const MoneySchema = z.object({
  amount: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/, 'amount must be decimal with up to 4 fractional digits'),
  currency: z
    .string()
    .length(3, 'currency must be a 3-letter ISO 4217 code')
    .regex(/^[A-Z]{3}$/, 'currency must be uppercase A-Z'),
});

export type Money = z.infer<typeof MoneySchema>;

export const ExchangeRateSchema = z.object({
  fromCurrency: z.string().length(3),
  toCurrency: z.string().length(3),
  rate: z.string().regex(/^\d+(\.\d{1,8})?$/, 'rate must be positive decimal'),
  asOf: z.string().datetime(),
  source: z.string().min(1),
});

export type ExchangeRate = z.infer<typeof ExchangeRateSchema>;

export const ConvertRequestSchema = z.object({
  amount: z.string().regex(/^-?\d+(\.\d{1,4})?$/),
  from: z.string().length(3),
  to: z.string().length(3),
  asOf: z.string().datetime().optional(),
});

export type ConvertRequest = z.infer<typeof ConvertRequestSchema>;

/** Staff-entered exchange rate (e.g. the day's USD→IQD street rate). */
export const ExchangeRateCreateSchema = z
  .object({
    fromCurrency: z.string().length(3).regex(/^[A-Z]{3}$/),
    toCurrency: z.string().length(3).regex(/^[A-Z]{3}$/),
    rate: z.string().regex(/^\d+(\.\d{1,8})?$/, 'rate must be a positive decimal'),
    /** Effective date-time; defaults to now. Lookups use the newest rate ≤ the entry date. */
    asOf: z.string().datetime().optional(),
  })
  .refine((v) => v.fromCurrency !== v.toCurrency, { message: 'currencies must differ' })
  .refine((v) => Number(v.rate) > 0, { message: 'rate must be positive' })
  // DECIMAL(19,8) holds 11 integer digits — reject anything larger up front
  // instead of letting MySQL throw an out-of-range 500.
  .refine((v) => Number(v.rate) < 1e11, { message: 'rate too large' });
export type ExchangeRateCreate = z.infer<typeof ExchangeRateCreateSchema>;

export const ConvertResponseSchema = z.object({
  input: MoneySchema,
  output: MoneySchema,
  rate: z.string(),
  asOf: z.string().datetime(),
  source: z.string(),
});

export type ConvertResponse = z.infer<typeof ConvertResponseSchema>;
